import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Database,
  Trash2,
  ChevronDown,
  ChevronRight,
  X,
  Loader2,
  Search,
  FolderOpen,
  Folder,
  Check,
  RefreshCw,
  Settings,
  Filter,
  FileText,
} from "lucide-react";
import documentStoresService, { FolderItem, UploadedFilePreview } from "../../../services/document-stores.service";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import NodeDocsButton from "../../docs/NodeDocsButton";

interface DocumentStore {
  _id: string;
  folder_name: string;
  folder_description?: string;
  document_store_type?: string;
  id: string;
}

interface FolderTreeNode {
  folder: FolderItem;
  children: FolderTreeNode[];
}

function buildFolderTree(folders: FolderItem[]): FolderTreeNode[] {
  const byParent = new Map<string | null, FolderItem[]>();
  for (const f of folders) {
    const key = f.parent_folder_id ?? null;
    const list = byParent.get(key) ?? [];
    list.push(f);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  const build = (parentId: string | null): FolderTreeNode[] =>
    (byParent.get(parentId) ?? []).map((f) => ({
      folder: f,
      children: build(f._id),
    }));
  return build(null);
}

function findBreadcrumb(folders: FolderItem[], folderId: string | null): FolderItem[] {
  if (!folderId) return [];
  const byId = new Map(folders.map((f) => [f._id, f] as const));
  const path: FolderItem[] = [];
  let cursor: string | null = folderId;
  let safety = 0;
  while (cursor && safety++ < 64) {
    const cur = byId.get(cursor);
    if (!cur) break;
    path.unshift(cur);
    cursor = cur.parent_folder_id;
  }
  return path;
}

export const DocumentStoreNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [documentStores, setDocumentStores] = useState<DocumentStore[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>(
    data?.document_store_id || "",
  );
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  // Store search (inside modal)
  const [storeSearch, setStoreSearch] = useState("");

  // Multi-select folder picker: empty set = all files, has ids = specific folders
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(() => {
    const init = new Set<string>();
    if (Array.isArray(data?.folder_ids)) {
      for (const f of data.folder_ids) if (typeof f === "string" && f) init.add(f);
    } else if (typeof data?.folder_id === "string" && data.folder_id) {
      init.add(data.folder_id);
    }
    return init;
  });
  const [includeSubfolders, setIncludeSubfolders] = useState<boolean>(
    data?.include_subfolders !== false,
  );

  const toggleFolderSelection = useCallback((folderId: string) => {
    setSelectedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const clearAllSelected = useCallback(() => setSelectedFolderIds(new Set()), []);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());


  const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const fetchDocumentStores = useCallback(async () => {
    setIsLoadingStores(true);
    try {
      const response = await documentStoresService.getDocumentStoresDropdown();
      if (response.success) setDocumentStores(response.data);
    } catch (error) {
      console.error("Failed to fetch document stores:", error);
    } finally {
      setIsLoadingStores(false);
    }
  }, []);

  useEffect(() => {
    if (documentStores.length === 0) fetchDocumentStores();
  }, [documentStores.length, fetchDocumentStores]);

  const selectedStoreDetails = useMemo(
    () => documentStores.find((store) => store.id === selectedStore),
    [documentStores, selectedStore],
  );

  const filteredStores = useMemo(() => {
    return documentStores.filter((store) =>
      store.folder_name.toLowerCase().includes(storeSearch.toLowerCase()),
    );
  }, [documentStores, storeSearch]);

  const statusConfig = useMemo(() => {
    return selectedStore
      ? { bg: "bg-emerald-500", text: "Ready" }
      : { bg: "bg-gray-400", text: "Idle" };
  }, [selectedStore]);

  const validateNode = useCallback(() => {
    const allNodes = getNodes();
    const edges = getEdges();
    const { errors } = getNodeValidationStatus(
      allNodes.find((n) => n.id === id)!,
      edges,
      allNodes,
    );
    setErrors(errors);
  }, [getNodes, getEdges, id]);

  // Sync local state with data prop changes (e.g. undo/redo)
  useEffect(() => {
    if (data?.document_store_id && data.document_store_id !== selectedStore) {
      setSelectedStore(data.document_store_id);
    }
  }, [data?.document_store_id]);

  // Fetch folder tree for the selected store
  const fetchFolders = useCallback(async (storeId: string) => {
    if (!storeId) {
      setFolders([]);
      return;
    }
    setIsLoadingFolders(true);
    try {
      const list = await documentStoresService.listAllFoldersForStore(storeId);
      setFolders(list || []);
      // Auto-expand the path to every selected folder so they're visible
      if (selectedFolderIds.size > 0) {
        const toExpand = new Set<string>();
        selectedFolderIds.forEach((fid) => {
          findBreadcrumb(list || [], fid).forEach((c) => toExpand.add(c._id));
        });
        setExpandedFolders(toExpand);
      }
    } catch (e) {
      console.error("Failed to load folders for store:", e);
      setFolders([]);
    } finally {
      setIsLoadingFolders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset and reload folders when store changes
  useEffect(() => {
    if (selectedStore) {
      fetchFolders(selectedStore);
    } else {
      setFolders([]);
      setSelectedFolderIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  // Drop any selected folder ids that don't belong to the current store
  useEffect(() => {
    if (selectedFolderIds.size > 0 && folders.length > 0) {
      const valid = new Set(folders.map((f) => f._id));
      let changed = false;
      const next = new Set<string>();
      selectedFolderIds.forEach((id) => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      if (changed) setSelectedFolderIds(next);
    }
  }, [folders, selectedFolderIds]);

  // Files actually emitted by the node's last execution (rendered in right column)
  const outputFiles = useMemo<UploadedFilePreview[]>(() => {
    const arr = (data as any)?.outputs?.file_ids;
    return Array.isArray(arr) ? (arr as UploadedFilePreview[]) : [];
  }, [data]);

  // Stamp node data — store + multi-folder filter — back into React Flow on any change
  useEffect(() => {
    const folder_ids_array = Array.from(selectedFolderIds);
    const has_selection = folder_ids_array.length > 0;
    setNodes((nds) =>
      nds.map((node: any) => {
        if (node.id !== id) return node;
        return {
          ...node,
          data: {
            ...node.data,
            document_store_id: selectedStore,
            folder_ids: folder_ids_array,
            // keep folder_id populated with the first picked id for backward compat
            folder_id: has_selection ? folder_ids_array[0] : null,
            include_subfolders: has_selection ? includeSubfolders : false,
          },
        };
      }),
    );
    validateNode();
  }, [selectedStore, selectedFolderIds, includeSubfolders, id, setNodes, validateNode]);

  const handleStoreSelect = (storeId: string) => {
    setSelectedStore(storeId);
    // Don't close the popup — user might still want to pick folders
    setStoreSearch("");
  };

  // Selected folders as ordered breadcrumb arrays — used by node card chip and popup header
  const selectedFolderPaths = useMemo(() => {
    return Array.from(selectedFolderIds).map((fid) => ({
      id: fid,
      path: findBreadcrumb(folders, fid).map((c) => c.name).join(" / "),
    }));
  }, [selectedFolderIds, folders]);

  // Summary text for the card chip
  const filterSummary = useMemo(() => {
    if (!selectedStoreDetails) return "No store selected";
    if (selectedFolderIds.size === 0) return "All files in store";
    if (selectedFolderIds.size === 1) return selectedFolderPaths[0]?.path || "(loading…)";
    return `${selectedFolderIds.size} folders selected`;
  }, [selectedStoreDetails, selectedFolderIds, selectedFolderPaths]);

  return (
    <>
      {/* Compact node card — header + summary chip + Configure button */}
      <div
        className={`relative bg-gradient-to-br from-blue-50 to-cyan-50 border-2 rounded-xl p-4 min-w-72 max-w-80 shadow-lg transition-all duration-200 ${
          !selectedStoreDetails && errors.length > 0
            ? "border-red-500"
            : selected
              ? "border-blue-500 shadow-2xl scale-105"
              : "border-blue-200 hover:shadow-xl"
        }`}
      >
        <div className="flex items-center justify-between mb-3 cursor-move">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 text-sm">DocIQ Trigger</h3>
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${statusConfig.bg} animate-pulse`} />
                <p className="text-xs text-blue-600">{statusConfig.text}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NodeDocsButton nodeType="DOCUMENT-STORE" />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteNode();
              }}
              className="text-red-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-all"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Compact summary chip */}
        <div className="space-y-2" onClick={stopPropagation}>
          <div className="bg-white/70 border border-blue-100 rounded-md p-2.5 text-xs">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              <Database className="w-3 h-3" />
              Store
            </div>
            <div className="text-sm font-medium text-gray-900 truncate">
              {selectedStoreDetails?.folder_name ?? (
                <span className="text-gray-400 italic font-normal">Not selected</span>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold mt-2 mb-1">
              <Filter className="w-3 h-3" />
              Folder filter
            </div>
            <div className="text-xs text-gray-700 flex items-start gap-1.5 flex-wrap">
              {!selectedStoreDetails ? (
                <span className="text-gray-400 italic">—</span>
              ) : selectedFolderIds.size === 0 ? (
                <span className="italic text-gray-500">All files in store</span>
              ) : (
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <FolderOpen className="w-3 h-3 text-blue-600 flex-shrink-0" />
                    <span className="font-medium text-blue-900 truncate">{filterSummary}</span>
                    {includeSubfolders && (
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px]">
                        + subfolders
                      </span>
                    )}
                  </div>
                  {selectedFolderIds.size > 1 && (
                    <div className="text-[10px] text-gray-500 max-h-10 overflow-y-auto pl-4 nowheel" onWheel={(e) => e.stopPropagation()}>
                      {selectedFolderPaths.map((p) => (
                        <div key={p.id} className="truncate">› {p.path}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsConfigOpen(true);
            }}
            className="w-full px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5"
          >
            <Settings className="w-3.5 h-3.5" /> Configure
          </button>

          {!selectedStoreDetails && errors.length > 0 && (
            <div className="bg-red-50 text-red-700 border border-red-200 text-xs p-2 rounded-lg">
              {errors.map((err, i) => (
                <div key={i}>• {err}</div>
              ))}
            </div>
          )}
        </div>

        <Handle
          type="source"
          position={Position.Right}
          style={{
            boxShadow:
              "0 0 0 4px rgba(7, 108, 131, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
          }}
        />
      </div>

      {/* ONE popup — 3-column: Store | Folder tree | Mode & selection */}
      {isConfigOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsConfigOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-[1400px] h-[85vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Configure DocIQ Trigger</h2>
                    <p className="text-xs text-gray-500">
                      Pick a Document Store, then optionally scope the trigger to a specific folder.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsConfigOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body — 3 columns */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">

                {/* LEFT COLUMN — Store picker */}
                <div className="w-80 shrink-0 bg-gray-50 flex flex-col overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Document Store
                      </h3>
                      <button
                        type="button"
                        onClick={() => fetchDocumentStores()}
                        className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        title="Reload stores"
                      >
                        <RefreshCw className={`w-3 h-3 ${isLoadingStores ? "animate-spin" : ""}`} />
                        Reload
                      </button>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search stores…"
                        value={storeSearch}
                        onChange={(e) => setStoreSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-2">
                    {isLoadingStores ? (
                      <div className="flex items-center justify-center py-10 text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading stores…
                      </div>
                    ) : filteredStores.length === 0 ? (
                      <p className="text-center text-gray-400 py-10 text-xs italic">No stores found</p>
                    ) : (
                      <div className="space-y-1">
                        {filteredStores.map((store) => {
                          const isSelected = selectedStore === store.id;
                          return (
                            <button
                              key={store.id}
                              type="button"
                              onClick={() => handleStoreSelect(store.id)}
                              className={`w-full text-left px-3 py-2 rounded-md border transition-colors flex items-center gap-2 ${
                                isSelected
                                  ? "bg-blue-50 border-blue-200 text-blue-900"
                                  : "bg-white border-gray-200 text-gray-800 hover:border-blue-200 hover:bg-blue-50/30"
                              }`}
                            >
                              <FolderOpen
                                className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-blue-600" : "text-emerald-500"}`}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{store.folder_name}</div>
                                {store.document_store_type && (
                                  <div className="text-[10px] text-gray-500">{store.document_store_type}</div>
                                )}
                              </div>
                              {isSelected && <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* CENTER COLUMN — Folder tree (multi-select) */}
                <div className="flex-1 bg-white flex flex-col overflow-hidden">
                  {/* Document Store name banner — prominent display of which store is in play */}
                  <div className="px-4 py-2.5 border-b border-gray-200 bg-emerald-50">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1 flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      Document Store
                    </div>
                    <div className="text-sm font-bold text-emerald-900 truncate">
                      {selectedStoreDetails?.folder_name ?? (
                        <span className="text-emerald-700 font-normal italic">Pick a store from the left column →</span>
                      )}
                    </div>
                    {selectedStoreDetails?.document_store_type && (
                      <div className="text-[10px] text-emerald-700 mt-0.5">{selectedStoreDetails.document_store_type}</div>
                    )}
                  </div>

                  <div className="px-4 py-3 border-b border-gray-200 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Pick folders <span className="text-gray-400 normal-case font-normal">(leave empty for all files)</span>
                      </h3>
                      <button
                        type="button"
                        onClick={() => fetchFolders(selectedStore)}
                        className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        title="Reload folder list"
                      >
                        <RefreshCw className={`w-3 h-3 ${isLoadingFolders ? "animate-spin" : ""}`} />
                        Reload
                      </button>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search folders…"
                        value={folderSearch}
                        onChange={(e) => setFolderSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  {/* Selected folders banner — shows what's picked (multi) */}
                  <div
                    className={`px-4 py-2.5 border-b border-gray-200 ${
                      selectedFolderIds.size > 0 ? "bg-blue-50" : "bg-amber-50"
                    }`}
                  >
                    {selectedFolderIds.size > 0 ? (
                      <>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 mb-1 flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Selected folders ({selectedFolderIds.size})
                          </span>
                          <button
                            type="button"
                            onClick={clearAllSelected}
                            className="text-[10px] text-blue-600 hover:text-blue-800 font-medium normal-case"
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="space-y-0.5 max-h-24 overflow-y-auto nowheel" onWheel={(e) => e.stopPropagation()}>
                          {selectedFolderPaths.map((p) => (
                            <div
                              key={p.id}
                              className="flex items-center gap-1.5 text-xs bg-white border border-blue-100 rounded px-2 py-1"
                            >
                              <FolderOpen className="w-3 h-3 text-blue-600 flex-shrink-0" />
                              <span className="text-blue-900 truncate flex-1">{p.path}</span>
                              <button
                                type="button"
                                onClick={() => toggleFolderSelection(p.id)}
                                className="text-blue-500 hover:text-red-600 flex-shrink-0"
                                title="Remove from selection"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">
                          No folder picked
                        </div>
                        <div className="text-xs text-amber-800">
                          The trigger will emit <span className="font-semibold">all files</span> in this store.
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto p-3">
                    {!selectedStoreDetails ? (
                      <p className="text-center text-gray-400 py-10 text-xs italic">
                        Select a Document Store first.
                      </p>
                    ) : isLoadingFolders ? (
                      <div className="flex items-center justify-center py-10 text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading folders…
                      </div>
                    ) : folders.length === 0 ? (
                      <p className="text-center text-gray-400 py-10 text-xs">
                        This store has no folders yet — output will include all files.
                      </p>
                    ) : (
                      <FolderTreeList
                        tree={buildFolderTree(folders)}
                        selectedFolderIds={selectedFolderIds}
                        expandedFolders={expandedFolders}
                        onToggleExpand={(fid) => {
                          setExpandedFolders((prev) => {
                            const next = new Set(prev);
                            if (next.has(fid)) next.delete(fid);
                            else next.add(fid);
                            return next;
                          });
                        }}
                        onToggleSelect={toggleFolderSelection}
                        searchTerm={folderSearch}
                      />
                    )}
                  </div>

                  {/* User-guide notes */}
                  <div className="border-t border-gray-200 bg-blue-50/40 px-4 py-2.5 text-[10.5px] text-gray-700 space-y-1">
                    <div className="flex items-start gap-1.5">
                      <span className="text-blue-600 font-bold mt-px">›</span>
                      <span><b>Click a folder</b> to add it; click again to remove. Pick as many as you need.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="text-blue-600 font-bold mt-px">›</span>
                      <span><b>No folder picked</b> = trigger emits every file in the store.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="text-blue-600 font-bold mt-px">›</span>
                      <span>Use <b>Include subfolders</b> to also pull files from any nested folders under each pick.</span>
                    </div>
                  </div>

                  {/* Inline subfolder toggle + clear all in center column footer */}
                  {selectedFolderIds.size > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={clearAllSelected}
                        className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                      >
                        Clear all ({selectedFolderIds.size})
                      </button>
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-700 flex-shrink-0 ml-3">
                        <input
                          type="checkbox"
                          checked={includeSubfolders}
                          onChange={(e) => setIncludeSubfolders(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Include subfolders
                      </label>
                    </div>
                  )}
                </div>

                {/* RIGHT COLUMN — Execution Output (mirrors other nodes' pattern) */}
                <div className="w-[28rem] shrink-0 bg-gray-50 flex flex-col overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-white">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                      Execution Output
                    </h3>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      The most recent output emitted by this trigger
                    </p>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {outputFiles.length === 0 ? (
                      <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg bg-white">
                        <FileText className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                        <p className="text-xs text-gray-400 italic">
                          No output yet — run the workflow to populate.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* file_ids — accordion: header summary + click to expand for full details */}
                        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                          <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                              file_ids
                            </span>
                            <span className="text-[10px] text-gray-400 font-mono">
                              Array · {outputFiles.length} {outputFiles.length === 1 ? "item" : "items"}
                            </span>
                          </div>
                          <div className="p-2 space-y-1.5">
                            {outputFiles.map((file: any, idx) => {
                              const fullPath = file.folder_id
                                ? findBreadcrumb(folders, file.folder_id).map((c) => c.name).join(" / ")
                                : "(store root)";
                              const sizeKb = file.file_size ? Math.round(file.file_size / 1024) : null;
                              return (
                                <details
                                  key={file._id ?? idx}
                                  className="group bg-gray-50 border border-gray-200 rounded text-xs open:bg-white open:border-blue-200 open:shadow-sm"
                                >
                                  <summary className="px-2 py-1.5 cursor-pointer list-none flex items-start gap-2 hover:bg-blue-50/40 rounded">
                                    <ChevronRight className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0 transition-transform group-open:rotate-90" />
                                    <span className="text-gray-400 font-mono text-[10px] mt-0.5 w-5 text-right flex-shrink-0">
                                      {idx}.
                                    </span>
                                    <FileText className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-gray-900 truncate font-medium" title={file.name}>
                                        {file.name}
                                      </div>
                                      <div className="text-[10px] text-gray-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                        <Folder className="w-2.5 h-2.5 flex-shrink-0" />
                                        <span className="truncate" title={fullPath}>
                                          {fullPath || "(store root)"}
                                        </span>
                                        {file.file_type && <span>• {file.file_type}</span>}
                                        {sizeKb !== null && <span>• {sizeKb} KB</span>}
                                        {file.overall_processed_status && (
                                          <span
                                            className={`ml-auto px-1 py-0.5 rounded text-[9px] ${
                                              file.overall_processed_status === "DONE"
                                                ? "bg-green-100 text-green-700"
                                                : "bg-amber-100 text-amber-700"
                                            }`}
                                          >
                                            {file.overall_processed_status}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </summary>

                                  {/* Expanded details */}
                                  <div className="border-t border-gray-100 px-3 py-2 space-y-1.5">
                                    {file.file_summary && (
                                      <div>
                                        <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold mb-0.5">
                                          Summary
                                        </div>
                                        <div className="text-[11px] text-gray-700 whitespace-pre-wrap break-words">
                                          {file.file_summary}
                                        </div>
                                      </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10.5px] pt-1">
                                      {(() => {
                                        // Prefer backend-enriched names (saved in execution history); fall back to FE lookups
                                        const docStoreName =
                                          file.document_store_name ??
                                          documentStores.find((s) => (s as any).id === file.document_store_id || s._id === file.document_store_id)
                                            ?.folder_name ??
                                          selectedStoreDetails?.folder_name ??
                                          null;
                                        const folderName =
                                          file.folder_name ??
                                          folders.find((f) => f._id === file.folder_id)?.name ??
                                          null;
                                        const folderPath = file.folder_path ?? fullPath;

                                        const rows: Array<[string, any]> = [
                                          ["Document Store", docStoreName],
                                          ["Folder Name", folderName ?? "(store root)"],
                                          ["Folder Path", folderPath],
                                          ["File ID", file._id],
                                          ["Folder ID", file.folder_id ?? "(root)"],
                                          ["Document Store ID", file.document_store_id],
                                          ["Type", file.file_type],
                                          ["Size", file.file_size ? `${file.file_size} bytes` : null],
                                          ["Pages", file.file_pages],
                                          ["Upload type", file.upload_type],
                                          ["Processing", file.file_processing_type],
                                          ["AI level", file.ai_performance_level],
                                          ["Status", file.overall_processed_status],
                                          ["Visual", file.visual_processing_status],
                                          ["RAG", file.rag_processing_status],
                                          ["Created", file.createdAt ? new Date(file.createdAt).toLocaleString() : null],
                                          ["Uploaded", file.uploaded_at ? new Date(file.uploaded_at).toLocaleString() : null],
                                        ];
                                        return rows
                                          .filter(([, v]) => v !== undefined && v !== null && v !== "")
                                          .map(([k, v]) => (
                                            <React.Fragment key={k}>
                                              <div className="text-gray-500 font-medium">{k}</div>
                                              <div className="text-gray-800 break-all font-mono">{String(v)}</div>
                                            </React.Fragment>
                                          ));
                                      })()}
                                    </div>

                                    {file.url && (
                                      <div className="pt-1">
                                        <a
                                          href={file.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[11px] text-blue-600 hover:text-blue-800 underline break-all"
                                        >
                                          Open file URL ↗
                                        </a>
                                      </div>
                                    )}

                                    {file.overall_processing && (
                                      <div className="bg-gray-50 border border-gray-100 rounded px-2 py-1.5 text-[10px] text-gray-600 mt-1">
                                        <div className="font-bold text-gray-700 mb-0.5">Overall processing</div>
                                        <div>Time taken: <span className="font-mono">{file.overall_processing.time_taken}s</span></div>
                                        {file.overall_processing.start_time && (
                                          <div>Start: <span className="font-mono">{new Date(file.overall_processing.start_time).toLocaleString()}</span></div>
                                        )}
                                        {file.overall_processing.end_time && (
                                          <div>End: <span className="font-mono">{new Date(file.overall_processing.end_time).toLocaleString()}</span></div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </details>
                              );
                            })}
                          </div>
                        </div>

                        {/* Scalar fields from outputs as small cards */}
                        {(data as any)?.outputs && (
                          <>
                            {[
                              ["document_store_id", (data as any).outputs.document_store_id],
                              ["folder_id", (data as any).outputs.folder_id],
                              ["folder_ids", (data as any).outputs.folder_ids],
                              ["include_subfolders", (data as any).outputs.include_subfolders],
                            ]
                              .filter(([, v]) => v !== undefined)
                              .map(([k, v]) => (
                                <div key={k as string} className="bg-white border rounded-lg shadow-sm overflow-hidden">
                                  <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{k}</span>
                                    <span className="text-[10px] text-gray-400 font-mono">
                                      {Array.isArray(v) ? `Array · ${v.length}` : v === null ? "null" : typeof v}
                                    </span>
                                  </div>
                                  <div className="px-3 py-2 text-xs font-mono text-gray-800 break-all">
                                    {Array.isArray(v) ? JSON.stringify(v) : v === null ? "null" : String(v)}
                                  </div>
                                </div>
                              ))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-3 flex justify-between items-center">
                <div className="text-[11px] text-gray-500">
                  {selectedStoreDetails ? <>Current: <span className="font-medium text-gray-700">{filterSummary}</span></> : "Pick a store to begin"}
                </div>
                <button
                  onClick={() => setIsConfigOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default DocumentStoreNode;

// ─────────────────────────────────────────────────────────────────────────────
// Recursive folder tree renderer
// ─────────────────────────────────────────────────────────────────────────────

interface FolderTreeListProps {
  tree: FolderTreeNode[];
  selectedFolderIds: Set<string>;
  expandedFolders: Set<string>;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (id: string) => void;
  searchTerm: string;
  depth?: number;
}

const FolderTreeList: React.FC<FolderTreeListProps> = ({
  tree,
  selectedFolderIds,
  expandedFolders,
  onToggleExpand,
  onToggleSelect,
  searchTerm,
  depth = 0,
}) => {
  const q = searchTerm.trim().toLowerCase();

  const branchHasMatch = (node: FolderTreeNode): boolean => {
    if (!q) return true;
    if (node.folder.name.toLowerCase().includes(q)) return true;
    return node.children.some(branchHasMatch);
  };

  return (
    <>
      {tree.filter(branchHasMatch).map((node) => {
        const hasChildren = node.children.length > 0;
        const isExpanded = q ? true : expandedFolders.has(node.folder._id);
        const isSelected = selectedFolderIds.has(node.folder._id);
        return (
          <div key={node.folder._id}>
            <div
              className={`flex items-center gap-1 px-1 py-1 rounded cursor-pointer ${
                isSelected ? "bg-blue-100 text-blue-800" : "hover:bg-gray-50 text-gray-700"
              }`}
              style={{ paddingLeft: `${depth * 14 + 4}px` }}
              onClick={() => onToggleSelect(node.folder._id)}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) onToggleExpand(node.folder._id);
                }}
                className={`w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${
                  hasChildren ? "text-gray-500 hover:text-gray-700" : "text-transparent cursor-default"
                }`}
              >
                {hasChildren &&
                  (isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
              </button>
              {/* Multi-select checkbox */}
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect(node.folder._id);
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
              />
              {isExpanded && hasChildren ? (
                <FolderOpen className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              ) : (
                <Folder className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              )}
              <span className="truncate text-xs" title={node.folder.name}>
                {node.folder.name}
              </span>
            </div>
            {isExpanded && hasChildren && (
              <FolderTreeList
                tree={node.children}
                selectedFolderIds={selectedFolderIds}
                expandedFolders={expandedFolders}
                onToggleExpand={onToggleExpand}
                onToggleSelect={onToggleSelect}
                searchTerm={searchTerm}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
};
